import { useState, useEffect, useCallback, useRef } from 'react';
import { Event, EventFilters, EventListResponse } from '../types/event';
import eventAPI from '../services/eventAPI';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { logError } from '../utils/errorLogger';

interface UseEventsOptions {
  initialFilters?: Partial<EventFilters>;
  pageSize?: number;
  autoFetch?: boolean;
}

interface UseEventsReturn {
  // Data
  events: Event[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  
  // State
  loading: boolean;
  isFiltering: boolean;
  error: string | null;
  
  // Filters
  filters: EventFilters;
  setFilters: (filters: Partial<EventFilters>) => void;
  clearFilters: () => void;
  
  // Pagination
  goToPage: (page: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  
  // Actions
  refetch: (forceRefresh?: boolean) => Promise<void>;
  createEvent: (eventData: any) => Promise<Event>;
  updateEvent: (id: string, eventData: any) => Promise<Event>;
  deleteEvent: (id: string) => Promise<void>;
  
  // Phase 2 Actions
  cancelEvent: (id: string, reason?: string) => Promise<Event>;
  rescheduleEvent: (id: string, newDate: string, newTime?: string) => Promise<Event>;
  archiveEvent: (id: string) => Promise<Event>;
  duplicateEvent: (id: string, newTitle?: string, newDate?: string) => Promise<Event>;
  reactivateEvent: (id: string) => Promise<Event>;
}

const defaultFilters: EventFilters = {
  search: '',
  status: undefined,
  category_id: undefined,
  series_id: undefined,
  location: undefined,
  date_from: undefined,
  date_to: undefined,
  created_by: undefined,
  page: 1,
  page_size: 9,
  ordering: undefined,
};

export const useEvents = (options: UseEventsOptions = {}): UseEventsReturn => {
  const {
    initialFilters = {},
    pageSize = 9,
    autoFetch = true,
  } = options;

  const { user } = useSelector((state: RootState) => state.auth);
  
  // State
  const [events, setEvents] = useState<Event[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false); // New state for filter-specific loading
  const [error, setError] = useState<string | null>(null);
  
  // Refs for debouncing and scroll position
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const scrollPositionRef = useRef<number>(0);
  
  // Filters and Pagination
  const [filters, setFiltersState] = useState<EventFilters>({
    ...defaultFilters,
    page_size: pageSize,
    ...initialFilters,
  });

  // Computed values
  const currentPage = filters.page || 1;
  const effectivePageSize = filters.page_size || pageSize;
  const totalPages = Math.ceil(totalCount / effectivePageSize);
  
  // Debug pagination calculation (commented out to prevent console spam)
  // console.log('📊 Pagination Debug:', {
  //   totalCount,
  //   configuredPageSize: pageSize,
  //   effectivePageSize,
  //   filtersPageSize: filters.page_size,
  //   calculatedPages: totalPages,
  //   currentPage
  // });

  // Fetch events
  const fetchEvents = useCallback(async (forceRefresh: boolean = false, isFilterChange: boolean = false) => {
    if (!user) return;

    if (isFilterChange) {
      setIsFiltering(true);
    } else {
      setLoading(true);
    }
    setError(null);
    
    try {
      const response: EventListResponse = await eventAPI.getEvents(filters, currentPage, filters.page_size || 9, forceRefresh);
      
      // Check if response has the expected structure
      if (response && typeof response === 'object') {
        // Handle different possible response structures
        if ('events' in response && Array.isArray(response.events)) {
          // Standard EventListResponse structure
          setEvents(response.events);
          setTotalCount(response.total || 0);
        } else if (Array.isArray(response)) {
          // Direct array response
          setEvents(response);
          setTotalCount(response.length);
        } else if ('results' in response && Array.isArray((response as any).results)) {
          // Django paginated response with 'results' key
          const paginatedResponse = response as any;
          setEvents(paginatedResponse.results);
          setTotalCount(paginatedResponse.count || paginatedResponse.results.length);
        } else if ('count' in response && 'results' in response) {
          // Django REST Framework pagination format
          const drfResponse = response as any;
          setEvents(drfResponse.results);
          setTotalCount(drfResponse.count);
        } else {
          logError('Response structure not recognized');
          setEvents([]);
          setTotalCount(0);
          setError('Unexpected API response format');
        }
      } else {
        logError('Unexpected response structure');
        setEvents([]);
        setTotalCount(0);
        setError('Invalid response format from server');
      }
    } catch (err: any) {
      logError('Error fetching events', err);
      
      // Handle 404 errors (invalid page) by redirecting to page 1
      if (err.response?.status === 404 && currentPage > 1) {
        setFiltersState(prev => ({ ...prev, page: 1 }));
        setError(null); // Clear the error since we're handling it
        return; // Exit early, the useEffect will trigger a new fetch
      }
      
      setError(err.response?.data?.message || err.message || 'Failed to fetch events');
    } finally {
      setLoading(false);
      setIsFiltering(false);
    }
  }, [filters, user, currentPage]);

  // Save scroll position before filter changes
  const saveScrollPosition = useCallback(() => {
    scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
  }, []);

  // Restore scroll position after filter changes
  const restoreScrollPosition = useCallback(() => {
    setTimeout(() => {
      window.scrollTo(0, scrollPositionRef.current);
    }, 100); // Small delay to ensure DOM is updated
  }, []);

  // Debounced auto-fetch on filter changes
  useEffect(() => {
    if (!autoFetch || !user) return;

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Save current scroll position
    saveScrollPosition();

    // Set up debounced fetch
    debounceTimerRef.current = setTimeout(() => {
      fetchEvents(false, true).then(() => {
        // Restore scroll position after successful fetch
        restoreScrollPosition();
      });
    }, 300); // 300ms debounce for better UX

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [fetchEvents, autoFetch, user, filters, saveScrollPosition, restoreScrollPosition]);

  // Filter management
  const setFilters = useCallback((newFilters: Partial<EventFilters>) => {
    setFiltersState(prev => {
      // Only reset to page 1 if non-search filters changed
      const shouldResetPage = newFilters.page === undefined &&
        Object.keys(newFilters).some(key => key !== 'search' && newFilters[key as keyof EventFilters] !== prev[key as keyof EventFilters]);

      const updatedFilters = {
        ...prev,
        ...newFilters,
        page: newFilters.page !== undefined ? newFilters.page : (shouldResetPage ? 1 : prev.page),
      };

      // Handle ordering: check if it was explicitly set to undefined (cleared)
      if ('ordering' in newFilters) {
        if (newFilters.ordering === undefined) {
          // Explicitly cleared - remove ordering
          delete updatedFilters.ordering;
        }
        // If ordering has a value, it's already in updatedFilters from the spread above
      } else if (prev.ordering) {
        // Not in newFilters and previous had ordering - preserve it
        updatedFilters.ordering = prev.ordering;
      }
      // If not in newFilters and no previous ordering, leave it undefined (no sort)

      // Remove undefined values to properly clear filters
      Object.keys(updatedFilters).forEach(key => {
        if (updatedFilters[key as keyof EventFilters] === undefined) {
          delete updatedFilters[key as keyof EventFilters];
        }
      });

      return updatedFilters;
    });
  }, []);

  const clearFilters = useCallback(() => {
    // Get today's date for default date_from (hide past events by default)
    const today = new Date().toISOString().split('T')[0];
    // Preserve ordering when clearing filters
    const preservedOrdering = filters.ordering || defaultFilters.ordering;
    setFiltersState({
      ...defaultFilters,
      page_size: pageSize,
      date_from: today, // Reset to today when clearing filters
      ordering: preservedOrdering, // Preserve current ordering
    });
  }, [pageSize, filters.ordering]);

  // Pagination
  const goToPage = useCallback((page: number) => {
    if (page >= 1) {
      // Always allow going to page 1, for other pages check against totalPages
      if (page === 1 || page <= totalPages) {
        setFilters({ page });
      } else if (totalPages > 0) {
        // If requested page is too high, go to the last valid page
        setFilters({ page: totalPages });
      } else {
        // If no pages available, go to page 1
        setFilters({ page: 1 });
      }
    }
  }, [totalPages, setFilters]);

  const nextPage = useCallback(() => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  }, [currentPage, totalPages, goToPage]);

  const previousPage = useCallback(() => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  }, [currentPage, goToPage]);

  // Manual refetch
  const refetch = useCallback(async (forceRefresh: boolean = false) => {
    await fetchEvents(forceRefresh);
  }, [fetchEvents]);

  // CRUD Operations
  const createEvent = useCallback(async (eventData: any): Promise<Event> => {
    setLoading(true);
    setError(null);
    
    try {
      const newEvent = await eventAPI.createEvent(eventData);
      await refetch(); // Refresh the list
      return newEvent;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to create event';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [refetch]);

  const updateEvent = useCallback(async (id: string, eventData: any): Promise<Event> => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedEvent = await eventAPI.updateEvent(id, eventData);
      
      // Update the event in the local list
      setEvents(prev => prev.map(event => 
        event.id === id ? updatedEvent : event
      ));
      
      return updatedEvent;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update event';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteEvent = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      await eventAPI.deleteEvent(id);
      
      // Remove the event from the local list
      setEvents(prev => prev.filter(event => event.id !== id));
      setTotalCount(prev => prev - 1);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to delete event';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Phase 2 Actions
  const cancelEvent = useCallback(async (id: string, reason?: string): Promise<Event> => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedEvent = await eventAPI.cancelEvent(id, { reason });
      
      // Update the event in the local list
      setEvents(prev => prev.map(event => 
        event.id === id ? updatedEvent : event
      ));
      
      return updatedEvent;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to cancel event';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const rescheduleEvent = useCallback(async (id: string, newDate: string, newTime?: string): Promise<Event> => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedEvent = await eventAPI.rescheduleEvent(id, { 
        new_date: newDate, 
        new_time: newTime 
      });
      
      // Update the event in the local list
      setEvents(prev => prev.map(event => 
        event.id === id ? updatedEvent : event
      ));
      
      return updatedEvent;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to reschedule event';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const archiveEvent = useCallback(async (id: string): Promise<Event> => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedEvent = await eventAPI.archiveEvent(id, {});
      
      // Update the event in the local list
      setEvents(prev => prev.map(event => 
        event.id === id ? updatedEvent : event
      ));
      
      return updatedEvent;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to archive event';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const duplicateEvent = useCallback(async (id: string, newTitle?: string, newDate?: string): Promise<Event> => {
    setLoading(true);
    setError(null);
    
    try {
      // eventAPI.duplicateEvent expects separate string parameters, not an object
      const duplicatedEvent = await eventAPI.duplicateEvent(id, newTitle, newDate);
      
      await refetch(); // Refresh the list to show the new event
      return duplicatedEvent;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to duplicate event';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [refetch]);

  const reactivateEvent = useCallback(async (id: string): Promise<Event> => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedEvent = await eventAPI.reactivateEvent(id, {});
      
      // Update the event in the local list
      setEvents(prev => prev.map(event => 
        event.id === id ? updatedEvent : event
      ));
      
      return updatedEvent;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to reactivate event';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // Data
    events,
    totalCount,
    currentPage,
    totalPages,
    
    // State
    loading,
    isFiltering,
    error,
    
    // Filters
    filters,
    setFilters,
    clearFilters,
    
    // Pagination
    goToPage,
    nextPage,
    previousPage,
    
    // Actions
    refetch,
    createEvent,
    updateEvent,
    deleteEvent,
    
    // Phase 2 Actions
    cancelEvent,
    rescheduleEvent,
    archiveEvent,
    duplicateEvent,
    reactivateEvent,
  };
};

export default useEvents;
